import React from 'react';
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApartmentRounded,
  UnfoldMoreRounded,
  AutoAwesomeMosaic,
  AltRouteRounded,
  SettingsRounded,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { settingsService, CompanySettings } from '../../services/settingsService';
import { getSubdomain } from '../../utils/subdomain';
import { useAuth } from '../../context/AuthContext';
import { useAppTheme } from '../../theme';
import { isModifiedClick } from './navConfig';

const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN'];

/**
 * Org / workspace context in the top bar.
 *
 * Shows the REAL registered entity (`/settings/company` → companyName) over the
 * tenant subdomain, falling back to the subdomain alone if that read fails or
 * the user has no rights to it — an org chip that renders "undefined" is worse
 * than one that renders the host.
 *
 * Branch switching is presented but DISABLED with an explanation: the ERP has
 * no multi-branch entity model yet (there is one company profile per tenant),
 * so a live switcher here would be a control that silently does nothing. When
 * branches land, this is the one place that changes.
 */
const OrgSwitcher: React.FC = () => {
  const { colors } = useAppTheme();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const workspace = React.useMemo(() => getSubdomain(), []);
  const isAdmin = ADMIN_ROLES.includes((user?.role || '').toUpperCase());

  // Company profile barely changes within a session — fetch once, never retry
  // and never surface an error: this is chrome, not a page.
  const { data } = useQuery<{ settings: CompanySettings }>({
    queryKey: ['settings', 'company'],
    queryFn: () => settingsService.getCompanySettings(),
    staleTime: Infinity,
    retry: false,
  });

  const orgName = data?.settings?.companyName || workspace || 'Workspace';
  const orgSub = workspace && data?.settings?.companyName ? workspace : user?.department || 'Organization';

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-label={`Workspace: ${orgName}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          display: { xs: 'none', lg: 'flex' },
          alignItems: 'center',
          gap: 1,
          maxWidth: 240,
          px: 1.25,
          py: 0.625,
          borderRadius: 2,
          cursor: 'pointer',
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.backgroundPaper,
          transition: 'background-color .15s ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '&:hover': { backgroundColor: colors.background },
          '&:focus-visible': { outline: `2px solid ${colors.primary}`, outlineOffset: 2 },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 26,
            height: 26,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1.5,
            color: '#fff',
            background: 'linear-gradient(135deg, #6366F1 0%, #2563EB 100%)',
          }}
        >
          <ApartmentRounded sx={{ fontSize: 15 }} />
        </Box>
        <Box sx={{ minWidth: 0, lineHeight: 1.1 }}>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {orgName}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              color: colors.textSecondary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {orgSub}
          </Typography>
        </Box>
        <UnfoldMoreRounded sx={{ fontSize: 16, color: colors.textSecondary, flexShrink: 0 }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1, minWidth: 264, borderRadius: 2 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {orgName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {orgSub}
          </Typography>
        </Box>
        <Divider />

        {/* Honest dead end: rendered, explained, and not clickable. */}
        <Tooltip title="Single-entity workspace — no additional branches are configured" placement="left">
          <Box>
            <MenuItem disabled sx={{ py: 1.25, gap: 0.5 }}>
              <ListItemIcon>
                <AltRouteRounded fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Switch branch</Typography>
            </MenuItem>
          </Box>
        </Tooltip>

        <MenuItem
          component={RouterLink}
          to="/settings"
          onClick={(e: React.MouseEvent) => {
            if (isModifiedClick(e)) return;
            setAnchorEl(null);
          }}
          sx={{ py: 1.25, gap: 0.5 }}
        >
          <ListItemIcon>
            <SettingsRounded fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Company profile</Typography>
        </MenuItem>

        {isAdmin && (
          <MenuItem
            component={RouterLink}
            to="/admin/modules"
            onClick={(e: React.MouseEvent) => {
              if (isModifiedClick(e)) return;
              setAnchorEl(null);
            }}
            sx={{ py: 1.25, gap: 0.5 }}
          >
            <ListItemIcon>
              <AutoAwesomeMosaic fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">Plan &amp; modules</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default OrgSwitcher;
